// 005 US1, FR-011..FR-013 (P-HOME-4): the selected child's published booklist on Home — title,
// "Recommended by", the items with Mandatory/Optional tags, and Add full set (mandatory items only,
// one server call). With nothing published: the empty state with Browse School Books.
import { Link } from "expo-router";
import { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";

import type { AppError } from "@/api/client";
import type { Api } from "@/api/types";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";

import { cartMessage, SHOP_COPY } from "./copy";
import { formatPaise } from "./format";
import { ProductCard } from "./ProductCard";
import { type ShopperStudent, useShopping } from "./shopApi";
import { useLines } from "./useLines";
import { classSectionText } from "./useShopper";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    section: { gap: t.space.sm, marginTop: t.space.md },
    heading: { textTransform: "lowercase", color: t.color.primaryText },
    grid: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    cell: { width: 170, flexGrow: 1, maxWidth: 260 },
    action: { alignSelf: "flex-start", minWidth: 260 },
  }),
);

type Booklist = Api["StudentBooklist"];

export function useBooklist(studentId: string | null) {
  const { client } = useShopping();
  const [state, setState] = useState<{
    studentId: string;
    booklist: Booklist | null;
    error: AppError | null;
  } | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!studentId) return;
    let live = true;
    client.getBooklist(studentId).then(
      (booklist) => live && setState({ studentId, booklist, error: null }),
      (error: AppError) =>
        live && setState({ studentId, booklist: null, error }),
    );
    return () => {
      live = false;
    };
  }, [client, studentId, reload]);
  const current = state && state.studentId === studentId ? state : null;
  return {
    ...current,
    loading: studentId !== null && current === null,
    retry: () => setReload((n) => n + 1),
  };
}

export function FullSetButton({
  booklist,
  studentId,
  studentName,
  disabled,
  short = false,
}: {
  booklist: Booklist;
  studentId: string;
  studentName: string;
  disabled: boolean;
  short?: boolean;
}) {
  const styles = useStyles();
  const lines = useLines(studentId, true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    tone: "success" | "error";
    text: string;
    ref?: string | null;
  } | null>(null);
  const { itemCount, totalInPaise } = booklist.fullSet;
  if (itemCount === 0) return null;
  const add = async () => {
    setBusy(true);
    setMessage(null);
    const result = await lines.addFullSet(studentId);
    setBusy(false);
    if (result.ok) {
      const changed = result.added + result.raised;
      setMessage({
        tone: "success",
        text:
          changed === 0
            ? SHOP_COPY.alreadyInCart
            : SHOP_COPY.fullSetAdded(changed, studentName.split(" ")[0]!),
      });
    } else {
      const m = cartMessage(result.error);
      setMessage({ tone: "error", text: m.text, ref: m.reference });
    }
  };
  return (
    <View style={styles.section}>
      <View style={styles.action}>
        <Button
          pending={busy}
          disabled={disabled || lines.disabled}
          onPress={() => void add()}
          testID="full-set"
        >
          {short
            ? SHOP_COPY.fullSetShort(itemCount)
            : SHOP_COPY.fullSet(itemCount, formatPaise(totalInPaise))}
        </Button>
      </View>
      {message ? (
        <InlineMessage
          tone={message.tone}
          detail={message.ref ? `Reference: ${message.ref}` : null}
        >
          {message.text}
        </InlineMessage>
      ) : null}
    </View>
  );
}

export function BooklistSection({
  student,
  schoolActive,
}: {
  student: ShopperStudent;
  schoolActive: boolean;
}) {
  const styles = useStyles();
  const { onBrowseBooks } = useShopping();
  const lines = useLines(student.id, schoolActive);
  const { booklist, error, loading, retry } = useBooklist(student.id);
  const cls = classSectionText(student.classLevel, student.section);
  const first = student.name.split(" ")[0]!;
  return (
    <View style={styles.section} testID="home-booklist">
      <Text
        weight="light"
        size="x3l"
        style={styles.heading}
        accessibilityRole="header"
        aria-level={2}
      >
        {SHOP_COPY.booklistTitle(first, cls)}
      </Text>
      {loading ? (
        <Text tone="muted">…</Text>
      ) : error ? (
        <InlineMessage
          tone="error"
          detail={error.requestId ? `Reference: ${error.requestId}` : null}
          action={
            <Button variant="outline" onPress={retry}>
              {SHOP_COPY.retry}
            </Button>
          }
        >
          {SHOP_COPY.loadFailed}
        </InlineMessage>
      ) : booklist && booklist.published ? (
        <>
          <Text tone="muted">
            {SHOP_COPY.recommendedBy(booklist.school.name)}
          </Text>
          {!schoolActive ? (
            <InlineMessage tone="warning">
              {SHOP_COPY.schoolNotActive(booklist.school.name)}
            </InlineMessage>
          ) : null}
          <View style={styles.grid}>
            {booklist.items.map((item) => {
              const line = lines.line(item.productId);
              return (
                <View key={item.productId} style={styles.cell}>
                  <ProductCard
                    product={{
                      id: item.productId,
                      name: item.name,
                      kind: item.kind,
                      publisher: item.publisher,
                      priceInPaise: item.unitPriceInPaise,
                      imageUrl: item.imageUrl,
                      orderable: true,
                      available: item.available,
                      booklist: {
                        mandatory: item.mandatory,
                        quantity: item.quantity,
                      },
                    }}
                    {...line}
                  />
                </View>
              );
            })}
          </View>
          <FullSetButton
            booklist={booklist}
            studentId={student.id}
            studentName={student.name}
            disabled={!schoolActive}
          />
        </>
      ) : (
        <>
          <Text tone="muted">{SHOP_COPY.noBooklist(cls)}</Text>
          <View style={styles.action}>
            {onBrowseBooks ? (
              <Button variant="outline" onPress={onBrowseBooks}>
                {SHOP_COPY.browseBooks}
              </Button>
            ) : (
              <Link href="/shop/SCHOOL_BOOKS" asChild>
                <Button variant="outline" accessibilityRole="link">
                  {SHOP_COPY.browseBooks}
                </Button>
              </Link>
            )}
          </View>
        </>
      )}
      {lines.error && lines.error.key !== "load" ? (
        <InlineMessage tone="error">
          {cartMessage(lines.error.error).text}
        </InlineMessage>
      ) : null}
    </View>
  );
}
